import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, MessageSquare, RotateCcw } from 'lucide-react'
import { fetchFeedback, markHandled, type FeedbackItem, type FeedbackKind } from '../api/feedback'

/**
 * Everything the pilot has told us (D-607).
 *
 * <b>A working list, not a report.</b> It opens on what is outstanding, newest
 * first, because the question somebody brings to this screen is "what is left"
 * — and marking one done is what makes the list empty as it is worked through
 * rather than growing into something nobody opens.
 *
 * <b>Owner-only</b>, because it names teachers and describes what is broken. A
 * candid channel a colleague might be reading is a channel nobody uses
 * candidly.
 */

/*
 * <b>Every row here also has a thread</b> (D-838): each one emailed the answerers,
 * carries the screen and the build, and is where the kind is kept for triage. The
 * answering happens in the conversation, not here.
 *
 * ⚠ <b>Nobody who answers support can open this screen</b> — it takes
 * <c>ViewFeedback</c>, which is owner-only, and answering support confers no
 * capability. So the tick is the owner's and nobody else's. That is a
 * <c>GAPS.md</c> row rather than a thing this file should work around.
 */
const KIND_LABEL: Record<FeedbackKind, string> = {
  Problem: 'Broken',
  Idea: 'Could be better',
  Request: 'Missing',
  Support: 'Needs help',
}

const KIND_STYLE: Record<FeedbackKind, string> = {
  Problem: 'bg-crit-bg text-crit',
  Idea: 'bg-surface-2 text-ink-2',
  Request: 'bg-warn-bg text-warn',
  Support: 'bg-accent-soft text-accent-2',
}

/** "14 Sep, 4:32pm" — the grain somebody triaging actually needs. */
function when(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export default function FeedbackView() {
  const [outstanding, setOutstanding] = useState(true)
  const queryClient = useQueryClient()

  const { data, isPending, isError } = useQuery({
    queryKey: ['feedback', outstanding],
    queryFn: () => fetchFeedback(outstanding),
  })

  const act = useMutation({
    mutationFn: ({ id, handled }: { id: string; handled: boolean }) => markHandled(id, handled),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['feedback'] }),
  })

  const items = data ?? []

  return (
    <div className="page-shell px-5 py-8">
      <header className="mb-5">
        <p className="text-[11px] font-bold tracking-[0.14em] text-accent uppercase">Admin</p>
        <h1 className="font-display text-2xl">Feedback</h1>
        <p className="mt-1.5 max-w-[44rem] text-[13.5px] text-ink-3">
          What people have told us from inside the app. Each one carries who sent it and the
          screen they were on, so nothing has to be chased up to be understood.
        </p>
      </header>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {[
          [true, 'Still to look at'],
          [false, 'Everything'],
        ].map(([value, label]) => (
          <button
            key={String(value)}
            type="button"
            onClick={() => setOutstanding(value as boolean)}
            className={`rounded-lg border px-3 py-1.5 text-[12.5px] font-semibold ${
              outstanding === value
                ? 'border-accent text-accent-2'
                : 'border-line-2 text-ink-3 hover:border-ink-3'
            }`}
          >
            {label as string}
          </button>
        ))}
      </div>

      {isPending && <p className="text-sm text-ink-3">Loading...</p>}
      {isError && (
        <p role="alert" className="text-sm text-crit">
          Could not load the feedback.
        </p>
      )}

      {data && items.length === 0 && (
        <div className="rounded-2xl border border-line bg-surface px-5 py-10 text-center">
          <MessageSquare size={20} strokeWidth={2} className="mx-auto mb-2 text-ink-3" />
          <p className="text-[13.5px] text-ink-3">
            {outstanding
              ? 'Nothing waiting. Everything sent so far has been dealt with.'
              : 'Nobody has sent anything yet.'}
          </p>
        </div>
      )}

      <ul className="flex flex-col gap-3">
        {items.map((item: FeedbackItem) => (
          <li
            key={item.id}
            className="rounded-2xl border border-line bg-surface px-4 py-3.5"
          >
            <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <span
                className={`shrink-0 rounded-md px-2 py-0.5 text-[11.5px] font-semibold ${KIND_STYLE[item.kind]}`}
              >
                {KIND_LABEL[item.kind]}
              </span>

              <span className="text-[13.5px] font-semibold">{item.who}</span>
              {item.role && <span className="text-[12px] text-ink-3">{item.role}</span>}

              <span className="text-[12px] text-ink-3">{when(item.createdAt)}</span>

              <button
                type="button"
                disabled={act.isPending}
                onClick={() => act.mutate({ id: item.id, handled: item.handledAt === null })}
                className="ml-auto flex shrink-0 items-center gap-1.5 rounded-lg border border-line-2 px-2.5 py-1.5 text-[12px] font-semibold text-ink-3 hover:border-accent hover:text-accent-2 disabled:opacity-50"
              >
                {item.handledAt === null ? (
                  <>
                    <Check size={13} strokeWidth={2.5} />
                    Done
                  </>
                ) : (
                  <>
                    <RotateCcw size={13} strokeWidth={2.5} />
                    Put it back
                  </>
                )}
              </button>
            </div>

            {/* Their words, wrapped as they typed them — a report loses its
                steps when the newlines go. */}
            <p className="text-[13.5px] whitespace-pre-wrap wrap-anywhere">{item.body}</p>

            <p className="mt-2 flex flex-wrap gap-x-3 text-[11.5px] text-ink-3">
              {item.path && <span className="wrap-anywhere">On {item.path}</span>}
              {item.email && <span className="wrap-anywhere">{item.email}</span>}
              {item.build && <span className="wrap-anywhere">Build {item.build}</span>}
            </p>
          </li>
        ))}
      </ul>
    </div>
  )
}
